import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusPill from "@/components/StatusPill";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { Search, DollarSign, CreditCard, Receipt, FileText, Clock, CheckCircle2, Upload, FileUp, Contact, FolderUp } from "lucide-react";
import KPICard from "@/components/KPICard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { InvoiceImportDialog, QuickBooksExportButton } from "@/components/InvoiceImportDialog";
import { NewInvoiceButton } from "@/components/NewInvoiceButton";
import { LegacyInvoiceDialog } from "@/components/LegacyInvoiceDialog";
import { ContactFillDialog } from "@/components/ContactFillDialog";
import { BulkLegacyInvoiceDialog } from "@/components/BulkLegacyInvoiceDialog";
import { MigrationProgressCard } from "@/components/MigrationProgressCard";

export default function Billing() {
    const [invoices, setInvoices] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [kpis, setKpis] = useState(null);
    const [pending, setPending] = useState({ count: 0, hold_hours: 24, invoices: [] });
    const [importOpen, setImportOpen] = useState(false);
    const [contactsOpen, setContactsOpen] = useState(false);
    const [openBulkLegacy, setOpenBulkLegacy] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [approving, setApproving] = useState(false);

    const load = async () => {
        const params = statusFilter ? { status: statusFilter } : {};
        const [inv, k, p] = await Promise.all([
            api.get("/invoices", { params }),
            api.get("/dashboard/kpis"),
            api.get("/invoices/pending-review").catch(() => ({ data: { count: 0, invoices: [] } })),
        ]);
        setInvoices(inv.data); setKpis(k.data); setPending(p.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const approveOne = async (id) => {
        setApproving(true);
        try {
            await api.post(`/invoices/${id}/approve`);
            toast.success("Invoice approved & sent");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
        } finally { setApproving(false); }
    };
    const approveAll = async () => {
        if (!window.confirm(`Send all ${pending.count} draft invoices now?`)) return;
        setApproving(true);
        try {
            const { data } = await api.post("/invoices/approve-all");
            toast.success(`Sent ${data.approved} invoices`);
            setReviewOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk approve failed");
        } finally { setApproving(false); }
    };

    const filtered = invoices.filter((i) => {
        if (!q) return true;
        const v = q.toLowerCase();
        return i.customer_name?.toLowerCase().includes(v) || i.invoice_number?.toLowerCase().includes(v);
    });

    const totalOpen = invoices.filter((i) => i.status === "open" || i.status === "overdue")
                              .reduce((s, i) => s + i.balance, 0);
    const totalPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

    const initiateCheckout = async (inv) => {
        try {
            const origin = window.location.origin;
            const { data } = await api.post(`/payments/checkout/invoice/${inv.id}`,
                { origin_url: origin });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Stripe error");
        }
    };

    return (
        <div data-testid="billing-page">
            <PageHeader
                title="Billing & Payments"
                subtitle="Invoices, AutoPay, statements and outstanding balances — automated."
                actions={
                    <div className="flex items-center gap-2">
                        <QuickBooksExportButton />
                        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                                data-testid="billing-import-btn">
                            <Upload className="w-3.5 h-3.5 mr-1" />
                            Import invoices
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setContactsOpen(true)}
                                data-testid="billing-fill-contacts-btn">
                            <Contact className="w-3.5 h-3.5 mr-1" />
                            Fill contacts CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setOpenBulkLegacy(true)}
                                data-testid="billing-bulk-legacy-btn">
                            <FolderUp className="w-3.5 h-3.5 mr-1" />
                            Bulk legacy
                        </Button>
                        <NewInvoiceButton onCreated={load} label="Legacy invoice" testId="legacy-invoice"
                                          icon={FileUp} variant="outline" Dialog={LegacyInvoiceDialog}
                                          successMsg={null} />
                        <NewInvoiceButton onCreated={load} />
                    </div>
                }
            />
            <ContactFillDialog open={contactsOpen} setOpen={setContactsOpen} />
            <BulkLegacyInvoiceDialog open={openBulkLegacy} setOpen={setOpenBulkLegacy} onDone={load} />
            <InvoiceImportDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                onImported={load}
            />
            <div className="px-8 py-6 space-y-6">
                <MigrationProgressCard refreshSignal={invoices} onChanged={load} />
                {/* Pending Review banner */}
                {pending.count > 0 && (
                    <div className="flex items-center justify-between p-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900"
                         data-testid="pending-review-banner">
                        <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                            <div>
                                <div className="font-semibold text-sm">
                                    {pending.count} draft invoice{pending.count === 1 ? "" : "s"} waiting for review
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Auto-approves after {pending.hold_hours}h. Add last-minute extra fees before they send.
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setReviewOpen(true)}
                                    data-testid="review-drafts-btn">
                                Review
                            </Button>
                            <Button size="sm" onClick={approveAll} disabled={approving}
                                    data-testid="approve-all-drafts-btn">
                                {approving ? "Sending…" : "Send all now"}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Draft-review drawer */}
                {reviewOpen && (
                    <Card data-testid="draft-review-panel">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="font-heading font-semibold">Pending invoices</div>
                                <Button size="sm" variant="ghost" onClick={() => setReviewOpen(false)}>Close</Button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border">
                                            <th className="text-left px-3 py-2 overline">Invoice</th>
                                            <th className="text-left px-3 py-2 overline">Customer</th>
                                            <th className="text-left px-3 py-2 overline">Created</th>
                                            <th className="text-right px-3 py-2 overline">Total</th>
                                            <th className="text-left px-3 py-2 overline">Extras</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pending.invoices.map((inv) => (
                                            <tr key={inv.id} className="border-b border-border hover:bg-secondary/50">
                                                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                                                <td className="px-3 py-2 font-medium">{inv.customer_name}</td>
                                                <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(inv.issued_date)}</td>
                                                <td className="px-3 py-2 text-right tabular">{fmtMoney(inv.total)}</td>
                                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                                    {inv.pending_charges_count > 0
                                                        ? `+${inv.pending_charges_count} extra`
                                                        : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <Button size="sm" variant="outline"
                                                            onClick={() => approveOne(inv.id)}
                                                            disabled={approving}
                                                            data-testid={`approve-${inv.id}`}>
                                                        <CheckCircle2 className="w-4 h-4 mr-1" />Send
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KPICard testid="billing-kpi-mtd" label="Revenue MTD"
                             value={fmtMoney(kpis?.revenue_mtd || 0)} icon={DollarSign}
                             delta="Paid invoices" deltaTone="success" />
                    <KPICard testid="billing-kpi-outstanding" label="Outstanding"
                             value={fmtMoney(totalOpen)} icon={Receipt}
                             delta={`${invoices.filter((i) => i.status === "overdue").length} overdue`}
                             deltaTone={invoices.filter((i) => i.status === "overdue").length ? "destructive" : "muted"} />
                    <KPICard testid="billing-kpi-paid" label="Paid this period"
                             value={fmtMoney(totalPaid)} icon={CreditCard} delta="All-time visible" />
                    <KPICard testid="billing-kpi-count" label="Invoices on file"
                             value={invoices.length} icon={FileText} delta="In current view" />
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search invoice # or customer..." value={q}
                               onChange={(e) => setQ(e.target.value)} className="pl-9"
                               data-testid="invoice-search" />
                    </div>
                    <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                        <SelectTrigger className="w-44" data-testid="invoice-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <Card>
                    <div className="overflow-x-auto" data-testid="invoices-table">
                        <table className="w-full text-sm">
                            <thead>
                            <tr className="border-b border-border surface">
                                <th className="text-left px-4 py-3 overline">Invoice</th>
                                <th className="text-left px-4 py-3 overline">Customer</th>
                                <th className="text-left px-4 py-3 overline">Issued</th>
                                <th className="text-left px-4 py-3 overline">Due</th>
                                <th className="text-right px-4 py-3 overline">Total</th>
                                <th className="text-right px-4 py-3 overline">Balance</th>
                                <th className="text-left px-4 py-3 overline">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                            </thead>
                            <tbody>
                            {filtered.map((inv) => (
                                <tr key={inv.id} className="border-b border-border hover:bg-secondary/50">
                                    <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                                    <td className="px-4 py-3 font-medium">{inv.customer_name}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.issued_date)}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.due_date)}</td>
                                    <td className="px-4 py-3 text-right tabular">{fmtMoney(inv.total)}</td>
                                    <td className="px-4 py-3 text-right tabular">
                                        <span className={inv.balance > 0 ? "font-semibold" : "text-muted-foreground"}>{fmtMoney(inv.balance)}</span>
                                    </td>
                                    <td className="px-4 py-3"><StatusPill status={inv.status} /></td>
                                    <td className="px-4 py-3 text-right">
                                        {(inv.status === "open" || inv.status === "overdue") && (
                                            <Button size="sm" variant="outline"
                                                    onClick={() => initiateCheckout(inv)}
                                                    data-testid={`pay-${inv.id}`}>
                                                Charge
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
}
